import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { getLoginData } from "../utility/AppStoreData";
import { streamService, CreateStreamDto } from "../services/streamService";

// Types matching backend entities
export type StreamStatus =
  | "pending"
  | "live"
  | "paused"
  | "completed"
  | "failed";
export type StreamerStatus = "idle" | "streaming";

export interface StreamPlatformData {
  streamKey?: string;
  ingestUrl?: string;
  liveStreamId?: string;
  liveStreamURL?: string;
  streamUrl?: string;
  error?: string;
}

export interface ActiveStream {
  id: string;
  userId: string;
  platforms: Record<string, StreamPlatformData>;
  metadata?: {
    title?: string;
    description?: string;
  };
  status: StreamStatus;
  createdAt: string;
  updatedAt: string;
}

export interface StreamerStatusResponse {
  status: StreamerStatus;
  activeStream?: ActiveStream;
}

interface StreamStatusContextType {
  // State
  streamerStatus: StreamerStatus;
  activeStream: ActiveStream | null;
  isLoading: boolean;
  error: string | null;
  accessToken: string | null;

  // Actions
  createStream: (data: CreateStreamDto) => Promise<ActiveStream | null>;
  updateStreamStatus: (status: StreamStatus) => Promise<boolean>;
  endStream: () => Promise<boolean>;
  refreshStatus: () => Promise<void>;
  clearError: () => void;

  // For NativeApplicationContext to notify status changes
  notifyNativeStreamingChange: (isStreaming: boolean) => void;
}

const StreamStatusContext = createContext<StreamStatusContextType | undefined>(
  undefined
);

interface StreamStatusProviderProps {
  children: ReactNode;
}

export const StreamStatusProvider: React.FC<StreamStatusProviderProps> = ({
  children,
}) => {
  const [streamerStatus, setStreamerStatus] = useState<StreamerStatus>("idle");
  const [activeStream, setActiveStream] = useState<ActiveStream | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Track if we're currently updating to avoid race conditions
  const isUpdatingRef = useRef(false);
  // Track previous streaming state for change detection
  const prevStreamingRef = useRef<boolean | null>(null);

  // Fetch access token on mount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const loginData = await getLoginData();
        if (loginData?.AccessToken) {
          setAccessToken(loginData.AccessToken);
        }
      } catch (err) {
        console.error("Failed to get login data:", err);
      }
    };
    fetchToken();
  }, []);

  // Refresh streamer status from backend
  const refreshStatus = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      const statusData: StreamerStatusResponse =
        await streamService.getStreamerStatus(accessToken);

      setStreamerStatus(statusData.status);
      setActiveStream(statusData.activeStream || null);
      setError(null);
    } catch (err: any) {
      console.error("Failed to refresh status:", err);
      setError(err.message || "Failed to get status");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  // Auto-refresh status when token becomes available
  useEffect(() => {
    if (accessToken) {
      refreshStatus();
    }
  }, [accessToken, refreshStatus]);

  // Create a new stream on the backend
  const createStream = useCallback(
    async (data: CreateStreamDto): Promise<ActiveStream | null> => {
      if (!accessToken) {
        setError("Not authenticated");
        return null;
      }

      try {
        setIsLoading(true);
        setError(null);

        const stream = await streamService.createStream(accessToken, data);

        setActiveStream(stream);
        setStreamerStatus("streaming");

        return stream;
      } catch (err: any) {
        console.error("Failed to create stream:", err);
        setError(err.message || "Failed to create stream");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  // Update stream status on the backend
  const updateStreamStatus = useCallback(
    async (status: StreamStatus): Promise<boolean> => {
      if (!accessToken || !activeStream) {
        setError("No active stream to update");
        return false;
      }

      if (isUpdatingRef.current) {
        console.log("Skipping status update - already updating");
        return false;
      }

      try {
        isUpdatingRef.current = true;
        setIsLoading(true);

        const updatedStream = await streamService.updateStreamStatus(
          accessToken,
          activeStream.id,
          status
        );

        setActiveStream(updatedStream);

        // Update streamer status based on stream status
        if (status === "completed" || status === "failed") {
          setStreamerStatus("idle");
          setActiveStream(null);
        }

        setError(null);
        return true;
      } catch (err: any) {
        console.error("Failed to update stream status:", err);
        setError(err.message || "Failed to update status");
        return false;
      } finally {
        setIsLoading(false);
        isUpdatingRef.current = false;
      }
    },
    [accessToken, activeStream]
  );

  // End the current stream
  const endStream = useCallback(async (): Promise<boolean> => {
    const success = await updateStreamStatus("completed");
    if (success) {
      setActiveStream(null);
      setStreamerStatus("idle");
    }
    return success;
  }, [updateStreamStatus]);

  // Called by NativeApplicationContext when streaming status changes
  const notifyNativeStreamingChange = useCallback(
    (isStreaming: boolean) => {
      // Only react to actual changes
      if (prevStreamingRef.current === isStreaming) return;
      prevStreamingRef.current = isStreaming;

      console.log("Native streaming status changed:", isStreaming);

      // If native app stops streaming but backend thinks we're live, mark as paused
      if (!isStreaming && activeStream && activeStream.status === "live") {
        console.log(
          "Native app stopped streaming - updating backend to paused"
        );
        updateStreamStatus("paused").catch(console.error);
      }

      // If native app starts streaming and we have an active stream, mark as live
      if (isStreaming && activeStream && activeStream.status !== "live") {
        console.log("Native app started streaming - updating backend to live");
        updateStreamStatus("live").catch(console.error);
      }
    },
    [activeStream, updateStreamStatus]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <StreamStatusContext.Provider
      value={{
        streamerStatus,
        activeStream,
        isLoading,
        error,
        accessToken,
        createStream,
        updateStreamStatus,
        endStream,
        refreshStatus,
        clearError,
        notifyNativeStreamingChange,
      }}
    >
      {children}
    </StreamStatusContext.Provider>
  );
};

export const useStreamStatus = (): StreamStatusContextType => {
  const context = useContext(StreamStatusContext);
  if (!context) {
    throw new Error(
      "useStreamStatus must be used within a StreamStatusProvider"
    );
  }
  return context;
};
